import { Link } from 'react-router-dom';

import { UploadFailureReason } from '../../../../../../../../../backend/src/types/types';
import { FAILURE_REASON_CONFIG } from '../utils';
import { FileHistoryStats } from '@/api/queries/useGetFileHistoryStats';
import { Icon } from '@/new/components/icon';
import { PopoverContent } from '@/new/components/shadcn/popover';

export function BreakdownRow({ label, value }: { label: string; value: number }) {
  const muted = value === 0;
  return (
    <div className="flex items-center justify-between">
      <span className={`text-xs ${muted ? 'text-gray-400' : 'text-gray-600'}`}>{label}</span>
      <span className={`text-xs font-medium ${muted ? 'text-gray-400' : 'text-gray-700'}`}>
        {value.toLocaleString()}
      </span>
    </div>
  );
}

export function PopoverLink({ to, children, newTab }: { to: string; children: React.ReactNode; newTab?: boolean }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
      <Icon className={`${newTab ? 'lucide--external-link' : 'lucide--arrow-right'} h-3 w-3`} />
    </Link>
  );
}

export function PopoverButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
    >
      {children}
      <Icon className="lucide--arrow-right h-3 w-3" />
    </button>
  );
}

interface BreakdownPopoverProps {
  stats: FileHistoryStats;
  align?: 'start' | 'center' | 'end';
  onViewAll?: () => void;
}

export function SkippedBreakdownPopover({ stats, align = 'center', onViewAll }: BreakdownPopoverProps) {
  return (
    <PopoverContent className="w-52" align={align}>
      <div className="space-y-1.5">
        {(Object.entries(stats.failuresByReason) as [UploadFailureReason, number][]).map(([reason, count]) => {
          const config = FAILURE_REASON_CONFIG[reason];
          return <BreakdownRow key={reason} label={config.label} value={count} />;
        })}
      </div>
      <div className="mt-3 border-t border-gray-100 pt-2">
        {onViewAll && <PopoverButton onClick={onViewAll}>View all skipped files</PopoverButton>}
      </div>
    </PopoverContent>
  );
}

export function DeletedBreakdownPopover({ stats, align = 'center', onViewAll }: BreakdownPopoverProps) {
  return (
    <PopoverContent className="w-52" align={align}>
      <div className="space-y-1.5">
        <BreakdownRow label="Deleted before submission" value={stats.deletionsByReason.staged} />
        <BreakdownRow label="Deleted after submission" value={stats.deletionsByReason.submitted} />
      </div>
      <div className="mt-3 border-t border-gray-100 pt-2">
        {onViewAll && <PopoverButton onClick={onViewAll}>View all deleted files</PopoverButton>}
      </div>
    </PopoverContent>
  );
}
